import React, { useEffect, useState, useRef } from 'react';
import { useTonConnectUI, useTonWallet } from '@tonconnect/ui-react';
import { Wallet, Check, X, Copy, ExternalLink, Sparkles, ChevronDown, RefreshCw } from 'lucide-react';
import { useGame, api } from '../context/GameContext';
import { toast } from 'sonner';
import thumb from '../utils/thumb';

const shortAddr = (addr) => addr ? `${addr.slice(0, 4)}…${addr.slice(-4)}` : '';

const TonWalletCard = () => {
  const { user, t, refreshUser, haptic, language } = useGame();
  const [tonConnectUI] = useTonConnectUI();
  const wallet = useTonWallet();
  const [busy, setBusy] = useState(false);
  const [nfts, setNfts] = useState([]);
  const [tonNetwork, setTonNetwork] = useState('mainnet');
  const [nftsExpanded, setNftsExpanded] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const lastSyncedRef = useRef(null);

  // Sync TonConnect wallet -> backend when it changes
  useEffect(() => {
    const tcAddress = wallet?.account?.address;
    if (!tcAddress) return;
    if (lastSyncedRef.current === tcAddress) return;
    lastSyncedRef.current = tcAddress;
    (async () => {
      try {
        await api.post('/users/me/ton-wallet', { address: tcAddress });
        await refreshUser();
        toast.success(t('wallet_connected') || 'TON wallet connected');
      } catch (err) {
        const msg = err?.response?.data?.detail || 'Failed to save wallet';
        toast.error(msg);
      }
    })();
  }, [wallet?.account?.address]);

  // Load network info + user's minted NFTs
  useEffect(() => {
    api.get('/ton/info').then(r => {
      if (r.data?.network) setTonNetwork(r.data.network);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user?.ton_wallet) {
      setNfts([]);
      return;
    }
    const load = async () => {
      try {
        const res = await api.get('/cards/me');
        const minted = (res.data || []).filter(c => c.nft_address);
        setNfts(minted);
      } catch (_) { /* noop */ }
    };
    load();
    const id = setInterval(load, 15000); // re-check every 15s while page open
    return () => clearInterval(id);
  }, [user?.ton_wallet, user?.cards_count]);

  const handleConnect = async () => {
    haptic();
    try {
      setBusy(true);
      await tonConnectUI.openModal();
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    haptic();
    try {
      setBusy(true);
      await tonConnectUI.disconnect().catch(() => {});
      await api.delete('/users/me/ton-wallet');
      lastSyncedRef.current = null;
      await refreshUser();
      toast.success(t('wallet_disconnected') || 'Wallet disconnected');
    } catch (err) {
      toast.error('Failed to disconnect');
    } finally {
      setBusy(false);
    }
  };

  const linked = user?.ton_wallet;

  const handleCopy = async () => {
    if (!linked) return;
    try {
      await navigator.clipboard.writeText(linked);
      toast.success(t('copied') || 'Copied');
    } catch {
      // ignore
    }
  };

  const handleSyncNfts = async () => {
    if (syncing || !linked) return;
    haptic();
    setSyncing(true);
    try {
      const res = await api.post('/users/me/sync-nfts');
      const claimed = res.data?.claimed ?? 0;
      const rehydrated = res.data?.rehydrated ?? 0;
      const already = res.data?.already_owned ?? 0;
      const notInWallet = res.data?.not_in_wallet ?? 0;
      const total = claimed + rehydrated;
      // A painting listed on Getgems / held in auction escrow sits on a
      // sale contract, not in the wallet — saying "all your N NFTs" with
      // the wallet count made the player's collection look smaller.
      const elsewhere = notInWallet > 0
        ? (language === 'ru'
          ? ` · ещё ${notInWallet} вне кошелька (продажа/аукцион)`
          : ` · ${notInWallet} outside the wallet (sale/auction)`)
        : '';
      if (total > 0) {
        toast.success(`✅ Added ${total} NFT${total > 1 ? 's' : ''} to your collection`);
        await refreshUser();
      } else if (already > 0) {
        toast.success(
          (language === 'ru'
            ? `${already} NFT из кошелька уже в коллекции`
            : `${already} NFT${already > 1 ? 's' : ''} from your wallet already in your collection`)
          + elsewhere
        );
      } else {
        toast(t('no_nfts_in_wallet') || 'No NFTs from this collection in your wallet');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div data-testid="ton-wallet-card" className="glass-card rounded-2xl p-5 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <Wallet className="w-5 h-5 text-[#0098EA]" />
        <h3 className="font-medium text-sm text-[#F5F5F7]">{t('ton_wallet') || 'TON Wallet'}</h3>
        {linked && (
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400">
            <Check className="w-3 h-3" />
            {t('connected') || 'Connected'}
          </span>
        )}
      </div>

      {linked ? (
        <div className="space-y-3">
          <button
            data-testid="ton-wallet-address"
            onClick={handleCopy}
            className="w-full flex items-center justify-between gap-2 p-3 rounded-xl bg-[#0a0a12] hover:bg-[#0e0e16] transition-colors"
          >
            <span className="font-mono text-xs text-[#A1A1AA] truncate">{shortAddr(linked)}</span>
            <Copy className="w-3.5 h-3.5 text-[#52525B] shrink-0" />
          </button>
          <button
            data-testid="ton-wallet-disconnect"
            disabled={busy}
            onClick={handleDisconnect}
            className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-medium bg-[#141420] hover:bg-[#1a1a28] text-[#A1A1AA] disabled:opacity-50"
          >
            <X className="w-3.5 h-3.5" />
            {t('disconnect') || 'Disconnect'}
          </button>

          {/* Minted NFTs collapsible */}
          {nfts.length > 0 && (
            <div data-testid="user-nfts-list" className="pt-3 mt-1 border-t border-white/10">
              <button
                data-testid="nfts-toggle"
                onClick={() => { haptic(); setNftsExpanded(v => !v); }}
                className="w-full flex items-center justify-between gap-2 py-1.5 group"
              >
                <div className="flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-[#0098EA]" />
                  <span className="text-xs font-medium text-[#F5F5F7]">
                    {t('your_nfts') || 'Your TON Artworks'} <span className="text-[#0098EA]">({nfts.length})</span>
                  </span>
                </div>
                <ChevronDown className={`w-4 h-4 text-[#52525B] transition-transform ${nftsExpanded ? 'rotate-180' : ''}`} />
              </button>

              {nftsExpanded && (
                <>
                  <div className="grid grid-cols-3 gap-2 mt-2.5">
                    {nfts.map((c) => (
                      <a
                        key={c.id}
                        data-testid={`nft-card-${c.id}`}
                        href={`https://${tonNetwork === 'testnet' ? 'testnet.' : ''}tonviewer.com/${c.nft_address}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="relative block group"
                        title={c.title}
                      >
                        <div className="aspect-[3/4] rounded-lg overflow-hidden bg-[#0a0a12] border border-[#0098EA]/30 group-hover:border-[#0098EA] transition-colors">
                          <img
                            src={thumb(c.image_url, 160)}
                            alt={c.title}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                        <div className="absolute top-1 right-1 bg-[#0098EA] text-white text-[8px] font-bold px-1 py-0.5 rounded flex items-center gap-0.5">
                          <Sparkles className="w-2 h-2" />
                          TON
                        </div>
                        <div className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity bg-black/70 rounded-full p-1">
                          <ExternalLink className="w-2.5 h-2.5 text-white" />
                        </div>
                      </a>
                    ))}
                  </div>
                  <p className="text-[10px] text-[#52525B] mt-2">
                    {t('nft_wallet_note') ||
                      'Telegram Wallet caches NFTs slowly — these always show your real on-chain holdings.'}
                  </p>
                </>
              )}
            </div>
          )}

          {/* Sync action — single button (Force Refresh removed: TonAPI
              has no public force-refresh API; the previous button was
              cosmetic. Cache updates within 6-12h on TonAPI's own crawler.) */}
          <button
            data-testid="ton-wallet-sync"
            disabled={syncing}
            onClick={handleSyncNfts}
            className="w-full inline-flex items-center justify-center gap-2 py-2 rounded-lg text-[11px] font-medium bg-[#0a0a12] hover:bg-[#0e0e16] text-[#A1A1AA] hover:text-[#F5F5F7] disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} />
            {syncing
              ? (t('syncing') || 'Syncing...')
              : (t('sync_nfts_from_wallet') || 'Sync TON from wallet')}
          </button>
        </div>
      ) : (
        <>
          <p className="text-xs text-[#A1A1AA] mb-3">
            {t('ton_wallet_hint') ||
              'Connect your TON wallet to mint paintings as dynamic NFTs (25 ⭐).'}
          </p>
          <button
            data-testid="ton-wallet-connect"
            disabled={busy}
            onClick={handleConnect}
            className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium text-white disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg,#0098EA,#0073C7)' }}
          >
            <Wallet className="w-4 h-4" />
            {busy ? '...' : (t('connect_ton_wallet') || 'Connect TON Wallet')}
          </button>
        </>
      )}
    </div>
  );
};

export default TonWalletCard;
